import { useMemo } from 'react'

import { useActivationGroupsQuery } from '@/features/activationGroup/hooks/useActivationGroupsQuery'
import type { ActivationGroup } from '@/features/activationGroup/types'
import type { MarketProgram } from '@/features/bidding/constants'

export type UseEnergyActivationGroupsQueryParams = Readonly<{
  countryCode: CountryIdentifier
  marketProgram: MarketProgram
}>

export type UseEnergyActivationGroupsQueryResult = Readonly<{
  activationGroups: ReadonlyArray<ActivationGroup>
  isLoading: boolean
  isError: boolean
}>

/**
 * Activation groups available for the current `(countryCode, marketProgram)`.
 *
 * Sources the full country-scoped list from `GET /activation-group`
 * (existing endpoint — only accepts `countryCode`) and filters client-side
 * by `marketProgram`. When the backend exposes a `marketProgram` query
 * param natively, the filter can move server-side without consumer changes.
 *
 * Used by the energy view to populate the AG multi-select with the *full*
 * master list rather than only the AGs that already have bids in the
 * current context.
 */
export function useEnergyActivationGroupsQuery(
  params: UseEnergyActivationGroupsQueryParams,
): UseEnergyActivationGroupsQueryResult {
  const { countryCode, marketProgram } = params

  const { activationGroups, isLoading, isError } = useActivationGroupsQuery(params, {
    enabled: countryCode !== null,
  })

  const filtered = useMemo<ActivationGroup[]>(
    () => (activationGroups ?? []).filter((ag) => ag.marketProgram === marketProgram),
    [activationGroups, marketProgram],
  )

  return {
    activationGroups: filtered,
    isLoading,
    isError,
  }
}
